import React from "react";
import { differenceInMonths } from "date-fns";
import { Employee } from "../../features/process/types/processTypes";
import { AuthUser } from "../../features/auth/types/authType";
import { getAmharicCalendar } from "../../utils/calendarUtility";

const arabicIntro =
  "يرغب الطرف الأول في إبرام عقد مع الطرف الثاني للعمل لديه في مهنة:[أدخل المهنة] وقد اعترف الطرفان بقدرتهما على إبرام هذا العقد، واتفقا على الشروط والأحكام التالية:";

export interface Props {
  employee: Employee;
  user: AuthUser;
  ForwardedRef?: {
    ref: React.RefObject<HTMLDivElement> | null;
    ref2: React.RefObject<HTMLDivElement> | null;
    ref3: React.RefObject<HTMLDivElement> | null;
    ref4: React.RefObject<HTMLDivElement> | null;
  };
}

export const KuwaitContractWebPage = ({
  employee,
  user,
  ForwardedRef = { ref: null, ref2: null, ref3: null, ref4: null },
}: Props) => {
  const { ref, ref2, ref3, ref4 } = ForwardedRef;
  const company = user && user.company ? user.company : null;
  const edu = employee.education?.educationLevel as string;
  const appliedCountry = employee.additionalData?.appliedCountry as string;

  const getArabicCountry = (country: string) => {
    switch (country) {
      case "kuwait":
        return "الكويت";
      case "jordan":
        return "الأردن";
      case "lebanon":
        return "لبنان";
      case "oman":
        return "عُمان";
      case "qatar":
        return "قطر";
      case "saudi_arabia":
        return "السعودية";
      case "syria":
        return "سوريا";
      case "uae":
        return "الإمارات العربية المتحدة";
      case "yemen":
        return "اليمن";
      default:
        return country;
    }
  };
  const getAmharicCountry = (country: string) => {
    switch (country) {
      case "kuwait":
        return "ኩዌት";
      case "jordan":
        return "ጆርዳን";
      case "lebanon":
        return "ሊባኖን";
      case "oman":
        return "ኦማን";
      case "qatar":
        return "ቅታሪ";
      case "saudi_arabia":
        return "ሳዩዲ አረብ";
      case "syria":
        return "ስሪያ";
      case "uae":
        return "ዩአረብ አረብ";
      case "yemen":
        return "የማን";
      default:
        return country;
    }
  };
  const getEnglishCountry = (country: string) => {
    switch (country) {
      case "kuwait":
        return "Kuwait";
      case "jordan":
        return "Jordan";
      case "lebanon":
        return "Lebanon";
      case "oman":
        return "Oman";
      case "qatar":
        return "Qatar";
      case "saudi_arabia":
        return "Saudi Arabia";
      case "syria":
        return "Syria";
      case "uae":
        return "UAE";
      case "yemen":
        return "Yemen";
      default:
        return country;
    }
  };
  const appliedCountryAmharic = appliedCountry
    ? getAmharicCountry(appliedCountry)
    : "";
  const appliedCountryArabic = appliedCountry
    ? getArabicCountry(appliedCountry)
    : "";
  const appliedCountryEnglish = appliedCountry
    ? getEnglishCountry(appliedCountry)
    : "";

  const getArabicTextArrays = (country: string) => {
    const arabicCountryName = getArabicCountry(country);

    return [
      ".1 الدولة المضيفة:",
      ".2 موقع العمل:",
      ".3 مدة العقد:",
      ".4 بدء العقد:",
      ".5 نوع المهنة:",
      ".6 الراتب الشهري:",
      ".7 طريقة الدفع: على أساس شهري، ويجب أن يتم الدفع في موعد أقصاه عشرة (10) أيام من نهاية الشهر.",
      `.8 ساعات العمل العادية: وفقاً لقانون ${arabicCountryName}`,
      `.9 أوقات الراحة: وفقاً لقانون ${arabicCountryName}`,
      `.10 العمل الإضافي: وفقاً لقانون ${arabicCountryName}`,
      "أ. العمل أكثر من العادي",
      "ب. العمل في يوم عطلة محدد والأعياد.",
      ".11 إجازة مدفوعة كاملة:",
      `أ. إجازة أسبوعية وفقاً لقانون ${arabicCountryName}؛`,
      `ب. إجازة سنوية وفقاً لقانون ${arabicCountryName}؛`,
      `ج. إجازة مرضية وفقاً لقانون ${arabicCountryName}؛`,
      `د. أخرى وفقاً لقانون ${arabicCountryName}؛`,
      ".12 تذكرة ذهاب وعودة مجانية",
      ".13 طعام مجاني، سكن مناسب أو بدل سكن.",
      ".14 خدمات طبية مجانية ومرافق بما في ذلك الأدوية.",
      ".15 تأمين الحياة الشخصية وحوادث العمل يكون على عاتق صاحب العمل.",
      ".16 في حالة وفاة الموظف خلال مدة هذا الاتفاق، سيتم إعادة جثته وممتلكاته الشخصية إلى نقطة الانطلاق. في حال عدم إمكانية إعادة الجثة، قد يتم التصرف بها بشكل مناسب بعد الحصول على موافقة مسبقة من أقرباء الموظف أو من البعثة الإثيوبية المعنية (سفارة إثيوبيا، البعثة الدائمة، القنصلية العامة، القائم بالأعمال أو القنصل العام الفخري).",
      ".17 يجب على صاحب العمل مساعدتهم في تحويل راتبه من خلال القنوات المصرفية المناسبة أو وسائل أخرى مصرح بها قانوناً.",
      ".18 إنهاء العقد: يمكن أن تشمل أسباب إنهاء عقد العمل ما يلي:",
      " أ. إنهاء من قبل صاحب العمل: يمكن لصاحب العمل إنهاء هذا العقد دون إشعار مسبق للموظف لأي من الأسباب التالية: misconduct خطير، عدم الامتثال لأوامر صاحب العمل القانونية، الإهمال المستمر للواجبات، الغياب، تسريب أسرار المؤسسة أو عندما ينتهك الموظف شروط هذا العقد.",
      " ب. إنهاء من قبل الموظف: يمكن للموظف إنهاء هذا العقد دون إشعار مسبق لصاحب العمل لأي من الأسباب التالية: إهانة خطيرة من صاحب العمل أو ممثليه، معاملة غير إنسانية وغير محتملة تعرض لها الموظف من قبل صاحب العمل أو ممثليه. يمكن للموظف إنهاء هذا العقد لأي سبب من الأسباب بعد تقديم إشعار كتابي مسبق قبل 30 يوماً لصاحب العمل.",
      ".19 التجديد: يمكن تجديد هذا العقد بالاتفاق المتبادل.",
      ".20 التعديل والتغيير: أي تعديلات أو تغييرات تتم في هذا العقد بطريقة غير مواتية للعمال دون موافقة وزارة العمل والمهارات الإثيوبية (MoLS) ستكون غير فعالة.",
      ".21 تسوية المنازعات: ستتم تسوية جميع المطالبات والشكاوى المتعلقة بعقد العمل للموظف وفقاً لسياسات الشركة وقواعدها وأنظمتها والقوانين. في حالة التسوية، يجب أن يتم الأمر بشكل ودي مع مشاركة البعثة الإثيوبية المعنية (السفارة، البعثة الدائمة، القنصلية العامة، القائم بالأعمال والقنصل العام الفخري).",
      ".22 في حال فشل التسوية الودية، سيتم تقديم الأمر إلى الهيئة الحكومية المختصة في (الدولة المضيفة) أو في إثيوبيا حسب خيار الطرف المشتكي.",
      ".23 تذكرة ذهاب وعودة مجانييجب على الموظف الالتزام بقواعد الشركة الخاصة بصاحب العمل والامتثال للقوانين السارية في الدولة المضيفة.",
      ".24 يتحمل صاحب العمل مسؤولية الحصول على تأشيرة الدخول الضرورية وتصريح العمل القانوني للموظف من الدولة المضيفة.",
      ".25 القانون المطبق: يتم تنظيم الشروط والأحكام الأخرى للتوظيف التي تتماشى مع الأحكام المذكورة أعلاه بموجب القوانين الدائمة.",
      ".26 إذا تم انتهاك هذا العقد من قبل صاحب العمل، فقد وافقت الوكالة على أن تكون مسؤولة بالتضامن.",
      ".27 تم إعداد هذا العقد من أربع (4) نسخ؛ ستحصل الوكالة وصاحب العمل والموظف على نسخة، وستبقى النسخة المتبقية في وزارة العمل والمهارات الإثيوبية (MoLS).",
    ];
  };

  const arabicTextArrays = appliedCountry
    ? getArabicTextArrays(appliedCountry)
    : [];

  const getEducationLevelCaps = (education: string) => {
    let educationCap = "";
    switch (education) {
      case "grade_eight_complete":
        educationCap = "Primary";
        break;
      case "secondary_complete":
        educationCap = "Secondary";
        break;
      case "vocational_complete":
        educationCap = "Vocational";
        break;
      case "college_complete":
        educationCap = "College";
        break;
      case "post_graduate":
        educationCap = "Post Graduate";
        break;
      case "other_education":
        educationCap = "Other Education";
        break;
    }
    return educationCap;
  };

  const agency = employee.owner;
  const formattedDate = new Date().toLocaleDateString("en-US", {
    year: "numeric",
    month: "long",
    day: "numeric",
  });

  const employeeProspect =
    employee && employee.prospects && employee.prospects[0]
      ? employee.prospects[0]
      : null;
  let agent = company && company.partners ? company.partners[0] : null;
  if (employeeProspect) agent = employeeProspect;

  const englishIntro = "";
  const englishTextArrays = [];

  const amharicIntro = "";
  const amharicTextArrays = [];

  if (
    employee &&
    employee.address &&
    employee.additionalData &&
    employee.contactPerson &&
    employee.contactPerson.address &&
    employee.visa &&
    employee.visa.address &&
    employee.passportNumber &&
    employee.passportNumber.length > 0 &&
    agency &&
    agent
  ) {
    const empFullName = employee.givenName + " " + employee.surName;
    //  width: 297mm;
    return (
      <>
        <style>{`
          .kuwait-contract-page {           
            margin: 0 auto;
            padding: 10px;
            background: white;
            font-family: "Open Sans", sans-serif;
            font-size: 13px;
            line-height: 1.5;
            position: relative;
            box-shadow: 0 0 10px rgba(0, 0, 0, 0.1);
            z-index: 1;
          }

          .contract-header {
            display: grid ;
            grid-template-columns: 1fr 1.5fr 1fr;
            border: 1px solid #000;
            margin-bottom: 2px;
          }

          .header-section {
            flex: 1;
            padding: 10px;
            border-right: 2px dashed #b2beb5;
            text-align: center;
            font-weight: bold;
            font-size: 14px;
            color: #008000;
          }

          .header-section:last-child {
            border-right: none;
          }

          .ethiopic-text {
            font-family: "Noto Sans Ethiopic", sans-serif;
          }

          .arabic-text {
            font-family: "Noto Naskh Arabic", sans-serif;
            direction: rtl;
          }

          .contract-content {
            border: 1px solid #000;
            margin-bottom: 2px;
          }

          .content-section {
            display: grid;
            grid-template-columns: 1fr 1.5fr 1fr;
            border-bottom: 1px solid #ccc;
          }

          .content-column {
            flex: 1;
            padding: 4px;
            border-right: 2px dashed #b2beb5;
            
            position: relative;
          }

          .content-column:last-child {
            border-right: none;
          }
          

          .section-title {
            background-color: #e0e0e0;
            padding: 5px;
            margin-bottom: 10px;
            font-weight: bold;
            border-bottom: 1px solid #000;
          }

          .field-row {
            display: flex;
            align-items: center;
            margin-bottom: 5px;
            min-height: 25px;
          }

          .field-label {
            margin-right: 10px;
            min-width: 70px;
            }
            
            .field-value {
              font-weight: bold;
            flex: 1;
            border-bottom: 1px solid #000;
            padding-bottom: 2px;
          }

          .signature-section {
            
            
            }
            
            .signature-row {
              display: flex;
              
          }

          .signature-cell {
            width:100%;
            padding: 0px;
            border: 1px solid #000;
            text-align: center;          
          }

          .signature-title {
            background-color: #e0e0e0;
            padding: 1px;
            font-weight: bold;
            text-align: center;
            }
            
            .signature-box {
            display:flex;
            justify-content:center;
              width: 100%;
              height: 80px;
              border-top: 2px solid #000;
              background-color: #fff;
              text-align: center;
          }

          .witnesses-section {
            border: 2px solid #000;
            margin-bottom: 20px;
          }

          .witnesses-content {
            display: grid;
            grid-template-columns: 1fr 1.5fr 1fr;
            padding: 10px;
          }

          .witnesses-column {
            flex: 1;
            padding: 5px;
          }

          .witnesses-title {
            background-color: #e0e0e0;
            padding: 5px;
            margin-bottom: 10px;
            font-weight: bold;
            text-align: center;
          }

          .witness-field {
            display: flex;
            margin-bottom: 5px;
            align-items: center;
          }

          .witness-label {
            margin-right: 10px;
            min-width: 80px;
            }
            
            .witness-value {
              flex: 1;
              font-weight: bold;
            border-bottom: 1px solid #000;
            padding-bottom: 2px;
          }
            /* Overlay Image Styles */
          .overlay-image-small {
            
            width: 150px ;
            height: 70px ;
            
            z-index: 999999 ;
            pointer-events: none ;
            transform: translateZ(0) ;
            will-change: transform ;
            }
            
            .overlay-image-small img {
                width: 100% ;
                height: 100% ;
                
            object-fit: contain ;
            position: relative ;
            z-index: 999999 ;
          }

          /* Overlay Image Styles */
          .overlay-image {
            position: absolute;
            top: 100px;
            left: 10px;
            width: 190px ;
            height: 190px ;
            opacity: 0.6 ;
            z-index: 999999 ;
            pointer-events: none ;
            transform: translateZ(0) ;
            will-change: transform ;
            }
            
            .overlay-image img {
                width: 100% ;
                height: 100% ;
                border-radius: 50%;
            object-fit: contain ;
            position: relative ;
            z-index: 999999 ;
          }
            /* Overlay Image Styles */
          .overlay-image-right {
            position: absolute;
            bottom: 100px;
            right: 10px;
            width: 190px ;
            height: 190px ;
            opacity: 0.6 ;
            z-index: 999999 ;
            pointer-events: none ;
            transform: translateZ(0) ;
            will-change: transform ;
            }
            
            .overlay-image-right img {
                width: 100% ;
                height: 100% ;
                border-radius: 50%;
            object-fit: contain ;
            position: relative ;
            z-index: 999999 ;
          }

          /* Alternative overlay positions */
          .overlay-center {
            position: fixed;
            top: 50%;
            left: 50%;
            transform: translate(-50%, -50%);
            width: 200px;
            height: 200px;
            opacity: 0.2;
            z-index: 9999;
            pointer-events: none;
          }

          .overlay-corner {
            position: fixed;
            top: 100px;
            left: 100px;
            width: 120px;
            height: 120px;
            opacity: 0.4;
            z-index: 9999;
            pointer-events: none;
          }

          /* Print styles */
          @media print {
            .kuwait-contract-page {
              width: 100%;
              margin: 0;
              padding: 0;
              box-shadow: none;
              z-index: 1;
            }

            .overlay-image {
              position: absolute !important;
              top: 50px !important;
              right: 50px !important;
              z-index: 999999 !important;
              opacity: 0.4 !important;
            }

            .overlay-center,
            .overlay-corner {
              position: absolute;
            }
            }
          `}</style>

        <div className="kuwait-contract-page" ref={ref}>
          {/* Contract Header */}
          <div className="contract-header">
            <div className="header-section ethiopic-text">
              የ{appliedCountryAmharic} ሀገር የስራ ውል
            </div>
            <div className="header-section">
              EMPLOYMENT CONTRACT IN THE STATE OF{" "}
              {appliedCountryEnglish?.toUpperCase()}
            </div>
            <div className="header-section arabic-text">
              عقد العمل في دولة {appliedCountryArabic}
            </div>
          </div>

          {/* Contract Content */}
          <div className="contract-content">
            {/* Introduction */}
            <div className="content-section">
              {/* Overlay Image - Positioned outside main container */}
              <div className="content-column ethiopic-text">
                <div className="section-title">መግቢያ</div>
                <p>
                  አሰሪ ከሰራተኛው ጋር በ
                  {employee.additionalData.appliedProfession?.toUpperCase()} ሙያ
                  እንዲሰራለት ይህን የስራ ውል ተስማምተዋል። ሁለቱም አካላት በሚከተሉት የውል ግዴታዎች እና
                  ሁኔታዎች ለመገዛት ፈቅደው ተስማምተዋል፤
                </p>
              </div>
              <div className="content-column">
                <div className="section-title">Preamble</div>
                <p>
                  The first party wishes to conclude a contract with the second
                  party to work for it in the profession of{" "}
                  <strong>
                    {employee.additionalData.appliedProfession?.toUpperCase()}
                  </strong>
                  . The parties acknowledged their capacity to conclude this
                  contract, they agreed upon the following terms and conditions:
                </p>
              </div>
              <div className="content-column arabic-text">
                <div className="section-title">مقدمة</div>
                <p>{arabicIntro}</p>
              </div>
            </div>

            {/* Employer Information */}
            <div className="content-section">
              <div className="content-column ethiopic-text">
                <div className="section-title">ሀ. አሰሪ</div>
                <div className="field-row">
                  <span className="field-label">ሙሉ ስም:</span>
                </div>
                <div className="field-row">
                  <span className="field-label">ዜግነት:</span>
                </div>
                <div className="field-row">
                  <span className="field-label">ፖ.ሳ.ቁ:</span>
                </div>
                <div className="field-row">
                  <span className="field-label">ስልክ ቁጥር:</span>
                </div>
              </div>
              <div className="content-column">
                <div className="section-title">1. Employer Information</div>
                <div className="field-row">
                  <span className="field-label">Name:</span>
                  <span className="field-value">{employee.visa.fullName}</span>
                </div>
                <div className="field-row">
                  <span className="field-label">Nationality:</span>
                  <span className="field-value">
                    {employee.additionalData.appliedCountry === "kuwait"
                      ? "KUWAITI"
                      : "JORDANI"}
                  </span>
                </div>
                <div className="field-row">
                  <span className="field-label">P.O.Box:</span>
                </div>
                <div className="field-row">
                  <span className="field-label">Phone Number:</span>
                  <span className="field-value">
                    {employee.visa.address.telephone || ""}
                  </span>
                </div>
              </div>
              <div className="content-column arabic-text">
                <div className="overlay-image-right" style={{ bottom: "10px" }}>
                  <img
                    src={agent.companyStamp || ""}
                    alt="Contract Watermark"
                  />
                </div>
                <div className="section-title">١. معلومات الطرف الأول</div>
                <div className="field-row">
                  <span className="field-label">اسم:</span>
                </div>
                <div className="field-row">
                  <span className="field-label">الجنسية:</span>
                </div>
                <div className="field-row">
                  <span className="field-label">صندوق البريد:</span>
                </div>
                <div className="field-row">
                  <span className="field-label">رقم الهاتف:</span>
                </div>
              </div>
            </div>

            {/* Employee Information */}
            <div className="content-section">
              <div className="content-column ethiopic-text">
                <div className="overlay-image">
                  <img
                    src={agency.companyStamp || ""}
                    alt="Contract Watermark"
                  />
                </div>
                <div className="section-title">ለ: ሠራተኛ</div>
                <div className="field-row">
                  <span className="field-label">ሙሉ ስም:</span>
                </div>
                <div className="field-row">
                  <span className="field-label">ዜግነት:</span>
                </div>
                <div className="field-row">
                  <span className="field-label">እድሜ:</span>
                </div>
                <div className="field-row">
                  <span className="field-label">ጾታ:</span>
                </div>
                <div className="field-row">
                  <span className="field-label">የት/ት ደረጃ:</span>
                </div>
                <div className="field-row">
                  <span className="field-label">የጋብቻ ሁኔታ:</span>
                </div>
                <div className="field-row">
                  <span className="field-label">ፓስፖርት ቁጥር:</span>
                </div>
              </div>
              <div className="content-column">
                <div className="section-title">2. Employee Information</div>
                <div className="field-row">
                  <span className="field-label">Name:</span>
                  <span className="field-value">{empFullName}</span>
                </div>
                <div className="field-row">
                  <span className="field-label">Nationality:</span>
                  <span className="field-value">
                    {employee.additionalData.nationality?.toUpperCase()}
                  </span>
                </div>
                <div className="field-row">
                  <span className="field-label">Age:</span>
                  <span className="field-value">
                    {Math.floor(
                      differenceInMonths(
                        new Date(),
                        new Date(employee.dateOfBirth as Date)
                      ) / 12
                    )}
                  </span>
                </div>
                <div className="field-row">
                  <span className="field-label">Sex:</span>
                  <span className="field-value">
                    {employee.sex && employee.sex.toUpperCase()}
                  </span>
                </div>
                <div className="field-row">
                  <span className="field-label">Educational level:</span>
                  <span className="field-value">
                    {getEducationLevelCaps(edu).toUpperCase()}
                  </span>
                </div>
                <div className="field-row">
                  <span className="field-label">Marital Status:</span>
                  <span className="field-value">
                    {employee.additionalData.maritalStatus?.toUpperCase()}
                  </span>
                </div>
                <div className="field-row">
                  <span className="field-label">Passport Number:</span>
                  <span className="field-value">{employee.passportNumber}</span>
                </div>
              </div>
              <div className="content-column arabic-text">
                <div className="section-title">٢. معلومات الموظف</div>
                <div className="field-row">
                  <span className="field-label">اسم:</span>
                </div>
                <div className="field-row">
                  <span className="field-label">الجنسية:</span>
                </div>
                <div className="field-row">
                  <span className="field-label">العمر:</span>
                </div>
                <div className="field-row">
                  <span className="field-label">الجنس:</span>
                </div>
                <div className="field-row">
                  <span className="field-label">المستوى التعليمي:</span>
                </div>
                <div className="field-row">
                  <span className="field-label">الحالة المهنية:</span>
                </div>
                <div className="field-row">
                  <span className="field-label">رقم الجواز:</span>
                </div>
              </div>
            </div>

            {/* Contract Terms */}
            <div className="content-section">
              <div className="content-column ethiopic-text">
                <div className="section-title">የውሉ ዝርዝሮች</div>
                <p>1. ተቀባይ ሀገር: </p>
                <p>2. የስራው ስፍራ: </p>
                <p>
                  3. የስራው ውል የሚቆይበት ጊዜ: ሁለት ዓመታት ሰራተኛው ለስራ ወደሚሰማራበት ስፍራ ለመሄድ
                  ከኢትዮጵያ ከሚነሳበት ጊዜ አንስቶ
                </p>
                <p>4. የሙያው አይነት: </p>
                <p>5. የወር ደሞዝ: </p>
                <p>6. የክፍያው አፈጻጸም፡ በየወሩ (ወሩ ካለቀበት ጊዜ አንስቶ እስከ 10 ቀናት ድረስ)</p>
                <p>7. መደበኛ የስራ ሰዓት፡ በ{appliedCountryAmharic} ህግ መሰረት</p>
                <p>8. የእረፍት ሰዓት፡ በ{appliedCountryAmharic} ህግ መሰረት</p>
                <p>9. የትርፍ ሰዓት የስራ ክፍያ፡ በ{appliedCountryAmharic} ህግ መሰረት</p>
              </div>
              <div className="content-column">
                <div className="section-title">Contract Terms</div>
                <p>
                  1. Host Country:{" "}
                  <strong>{appliedCountryEnglish?.toUpperCase()}</strong>
                </p>
                <p>
                  2. Employment Site:{" "}
                  <strong>
                    {(
                      employee.visa.address.city ||
                      appliedCountryEnglish + " City"
                    ).toUpperCase()}
                  </strong>
                </p>
                <p>
                  3. Duration of the contract:<strong> Two years</strong>{" "}
                  starting from Employee's departure from Ethiopia to the
                  employment site
                </p>
                <p>
                  4. Profession type:{" "}
                  <strong>
                    {employee.additionalData.appliedProfession?.toUpperCase()}
                  </strong>
                </p>
                <p>
                  5. Monthly salary:{" "}
                  <strong>
                    {" "}
                    {employee.additionalData.salary}{" "}
                    {employee.additionalData.currency === "kuwait_dinar"
                      ? "KWD"
                      : "USD"}
                  </strong>
                </p>
                <p>
                  6. Payment modality: on a monthly basis and the payment shall
                  be no later than ten (10) days from the end of the month.
                </p>
                <p>
                  7. Normal working hours: as per the state of{" "}
                  {appliedCountryEnglish}'s law
                </p>
                <p>
                  8. Rest time: as per the State of {appliedCountryEnglish}'s
                  law
                </p>
              </div>
              <div className="content-column arabic-text">
                <div className="section-title">شروط العقد</div>
                {arabicTextArrays.slice(0, 9).map((text, idx) => (
                  <p key={idx}>{text}</p>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="kuwait-contract-page" ref={ref2}>
          {/* Contract Terms */}
          <div className="contract-content">
            <div className="content-section">
              <div className="content-column ethiopic-text">
                <div className="overlay-image">
                  <img
                    src={agency.companyStamp || ""}
                    alt="Contract Watermark"
                  />
                </div>
                <div className="section-title">የውሉ ዝርዝሮች</div>

                <p> ሀ. ከመደበኛው የስራ ሰዓት በላይ ለሚሰራ ስራ</p>
                <p> ለ. በተወሰነ የእረፍት እና የበዓል ቀናት ለሚሰራ ስራ</p>
                <p>10.እረፍት ከሙሉ ክፍያ ጋር፡</p>
                <p> ሀ. የሳምንት እረፍት፡ በ{appliedCountryAmharic} ህግ መሰረት</p>
                <p> ለ. የአመት ፍቃድ፡ በ{appliedCountryAmharic} ህግ መሰረት</p>
                <p> ሐ. የህመም ፍቃድ፡ በ{appliedCountryAmharic} ህግ መሰረት</p>
                <p> መ. ሌሎች፡ በ{appliedCountryAmharic} ህግ መሰረት</p>
                <p>11.ነጻ የደርሶ መልስ ትኬት</p>
                <p>12. በቂ የተመጣጠነ ነጻ ምግብ እና ምቹ የመኖሪያ ቤት ወይም ማካካሻ አበል</p>
                <p>13. ነጻ የህክምና አገልግሎት ከመድሀኒት አቅርቦት ጭምር</p>
                <p>14. በአሰሪው የተገባ የሰራተኛው የግል የህይወት እና የአደጋ ኢንሹራንስ</p>
                <p>
                  15. በዚህ የስራ ውል ቆይታ ጊዜ ውስጥ ሰራተኛው ቢሞት አስክሬኑ እና የግል ንብረቶቹ በአሰሪው
                  ወጪ ወደ ኢትዮጵያ መመለስ ይኖርበታል። ምናልባት አስክሬኑን ወደ ኢትዮጵያ መላክ የማይችል ሆኖ
                  ሲገኝ የሟችን የቅርብ ዘመድ እና/ወይም የኢትዮጵያ ሚሲዮን (ኤምባሲ፣ ቋሚ መልክተኛ ጽ/ቤት ጉዳይ
                  አስፈጻሚ ወይም የክብር ቆንስላ) በሚያደርጉት የቅድሚያ ስምምነት መሰረት ተገቢውን ማድረግ ይቻላል።
                </p>
                <p>
                  16. ሰራተኛው አግባብ ባለው የባንክ አሰራር ወይም ህጋዊ በሆነ በሌላ መንገድ በመጠቀም ደመዎዙን
                  ወደ አገሩ መላክ እንዲችል አሰሪው መርዳት ይኖርበታል።
                </p>
                <p>17. የሰራ ዉል መቋረጥ የስራ ዉሉ የሚቋረጥበት ምክንያቶች የሚከተሉተን ሊጨምር ይችላል፤</p>
                <p>
                  {" "}
                  ሀ. በአሰሪዉ የሚደረግ የስራ ዉል መቋረጥ፡ አሰሪዉ አንዳችም ማስጠንቀቂ ለሰራተኛዉ ሳሰጥ ይህንን
                  የስራ ዉል በሚከተሉት በቂ ምክንያቶች ሊቋረጥ ይችላል፤በሰራተኛዉ ጉልህ የስነ ምግባር ብልሹነት
                  የአሰሪዉን ህጋዊ ትዕዛዝ ሆነ ብሎ ያለመወጣት ልምድ ያለበቂ ምክንያት በተደጋጋሚ ከስራ የመቅረት
                  ፡፡የድርጅቱን ሚስጥሮች የመግለጽ የመሳሰሉት ወይም ይህን የስራ ዉል ሲሰጥ።
                </p>
              </div>
              <div className="content-column">
                <div className="section-title">Contract Terms</div>

                <p>
                  9. Overtime: as per the State of {appliedCountryEnglish}'s law
                </p>
                <p> a. To work more than normal</p>
                <p> b. To work on a specific day off and holidays.</p>
                <p>10. Full Payment Leave:</p>
                <p>
                  {" "}
                  a. Weekly leave as per the State of {appliedCountryEnglish}'s
                  law;
                </p>
                <p>
                  {" "}
                  b. Annual vacation as per the State of {appliedCountryEnglish}
                  's law;
                </p>
                <p>
                  {" "}
                  c. Sick leave as per the State of {appliedCountryEnglish}'s
                  law; and
                </p>
                <p>
                  {" "}
                  d. Others as per the State of {appliedCountryEnglish}'s law.
                </p>
                <p>11. Free round-trip ticket</p>
                <p>
                  12. Free, Adequate and balanced Food, housing allowance or
                  compensation allowance.
                </p>
                <p>
                  13. Free medical services and facilities including medicine.
                </p>
                <p>
                  14. Personal life and accident insurances covered by the
                  Employer.
                </p>
                <p>
                  15. In the event of death of the Employee during the terms of
                  this agreement, his remains and personal belongings shall be
                  repatriated to the point of origin. In case the repatriation
                  of remains is not possible, the same may be disposed of as
                  appropriate upon prior approval of the Employee's next of kin
                  and/or by the appropriate Ethiopian mission (Ethiopian Embassy
                  permanent mission general consulate office charge de affairs
                  or honorary consulate general).
                </p>
                <p>
                  16. The Employer shall assist them in remitting his salary
                  through the proper banking channel or other means authorized
                  by law.
                </p>
                <p>
                  17. Termination: grounds for termination of the employment
                  contract may include the following:
                </p>
                <p>
                  {" "}
                  a. Termination by the Employer: the Employer may terminate
                  this contract without serving any notice to the Employee for
                  any of the following just cases; serious misconduct,
                  disobedience of Employer's lawful orders, habitual neglect of
                  duties, absenteeism, reveling secrets of establishment or when
                  the Employee violates terms of this contract of agreement.
                </p>
              </div>
              <div className="content-column arabic-text">
                <div className="overlay-image-right">
                  <img
                    src={agent.companyStamp || ""}
                    alt="Contract Watermark"
                  />
                </div>
                <div className="section-title">شروط العقد</div>
                {arabicTextArrays.slice(9, 25).map((text, idx) => (
                  <p key={idx}>{text}</p>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="kuwait-contract-page" ref={ref3}>
          {/* Contract Terms */}
          <div className="contract-content">
            <div className="content-section">
              <div className="content-column ethiopic-text">
                <div className="overlay-image">
                  <img
                    src={agency.companyStamp || ""}
                    alt="Contract Watermark"
                  />
                </div>
                <div className="section-title">የውሉ ዝርዝሮች</div>
                <p>
                  {" "}
                  ለ. በሰራተኛዉ የሚደርግ የስራ ዉል መቋረጥ፡ ሰራተኛዉ አንዳችም ማስጠንቀቂያ ለአሰሪዉ ሳይሰጥ
                  ይህንን የስራ ዉል በሚከተሉት ምክንቶች ሊያቋርጥ ይችላል፤ የአሰሪዉ ወይም የወኪሉ ከባድ ስድብ
                  አሰሪዉ ወይም ወኪሉ ለሰራተኛዉ የሚያደርገዉ ኢሰብአዊነትና አግባብነት የሌለዉ አያያዝ፣ የአሰሪዉ
                  ወይም የወኪሉን በሰራተኛዉ ላይ የወንጀል ድርጊት መፈጸም ወይም የወኪሎች ህጎችን የዚህን የስራ ዉል
                  መጣስ። ሰራተኛዉ የ30 ቀን የቅድሚ የጹሁፍ ማስጠንቀቂያ ለአሰሪዉ በመስጠት ይህንን የስራ ዉል
                  በማነኛቸዉም ምክንያት ማቋረጥ ይችላል።
                </p>
                <p>18. እድሳት፡ ይህ የስራ ዉል በጋራ ስምምነት ሊታደስ ይችላል።</p>
                <p>
                  19. ማሻሻል እና መለወጥ፡ በዚህ የስራ ዉል ላይ ሊደረግ የሰራተኛዉን ጥቅም በሚቀንስ ማሻሻያ
                  ለዉጥ እና የመሳሰሉት፤ የሥራና ክህሎት ሚኒስቴር ፈቃድ ከሌለዉ በቀር ዉጤት አይኖረዉም።
                </p>
                <p>
                  20. የስራ ክርክር/ግጭት አፈታት፡ ማንኛዉም ከስራ ዉል ጋር የተዛመደ የይገባኛል ጥያቄ እና ቅሬታ
                  በኩባንያው ፖሊሲ፣መመሪያ፣ ደንብ እና ህጎች መሰረት ይፈታል። ይህ ሳይሳካ በቀረ ጊዜ አግባብ ያለዉ
                  የኢትዮጵያ ሚሲዮን (የኢትዮጵያ ኤምባሲ ቋሚ መልክተኛ ጽ/ቤት፣ ጉዳይ አስፈፃሚ ወይም የክብር
                  ቆንጽላ) ተሳታፊ በሆነበት ጉዳዩ በስምምነት እነዲፈታ ጥረት ያደርጋል።
                </p>
                <p>
                  21. ጥረቱ ካልተሳካም ጉዳዩ ቅሬታ ባለዉ ወገን ምርጫ በተቀባይ አገር ወይም በኢትዮጵያ ለሚገኘዉ
                  ባለስልጣን ወይም አግባብ ለሆነ የመንግሰት አካል እንዲቀርብ ይደረጋል።
                </p>
                <p>
                  22. ሰራተኛዉ የአሰሪዉን ኩባንያ መመሪያ ማክበር እና አግባብ ላለዉ የተቀባዩ አገር ህጎች መገዛት
                  ይኖርበታል።
                </p>
                <p>
                  23. አሰሪዉ ለሰራተኛዉ አስፈላጊዉን የመግቢያ ቪዛ እና ህጋዊ የስራ ፍቃድ ከተቀባዪ አገር
                  የማግኘት ሀላፊነት አለበት።
                </p>
                <p>
                  24. ተፈጻሚነት ያለዉ ህግ፡ ከላይ ከተመለከቱት አንቀጾች ጋር የሚጣጣሙ ሌሎች ግዴታዎችና የስራ
                  ሁኔታዎች አግባብ ባላቸዉ ህጎች ተፈጻሚነት ይኖራቸዋል።
                </p>
                <p>25. አሰሪዉ ይህንን የስራ ዉል ቢጥስ በጋራ እና በተናጠል ተጠያቂ ለመሆን ተስማምቷል።</p>
                <p>
                  26. ይህ የሰራ ውል በአራት ቅጂ ተዘጋጅቶ፤ ኤጀንሲዉ፣ አሰሪዉ እና ሰራተኛዉ አንድ አንድ
                  ይደርሳቸዋል። ቀሪው አንድ ኮፒ በስራ እና ክህሎት ሚኒስቴር እንዲቀመጥ ይደረጋል።
                </p>
              </div>
              <div className="content-column">
                <div className="section-title">Contract Terms</div>
                <p>
                  {" "}
                  b. Termination by the Employee: the Employee may terminate
                  this contract without serving any notice to the Employer for
                  any of the following just cases; serious insult by the
                  Employer or his representatives, inhuman and unbearable
                  treatment accorded to the Employee by the Employer or his
                  representative. The Employee may terminate this contract for
                  any reason by serving 30 days advance written notice to the
                  Employer.
                </p>
                <p>
                  18. Renewal: this contract may be renewed by the mutual
                  agreement.
                </p>
                <p>
                  19. Modification and alteration: any modification and
                  alternations and the like made in this contract unfavorable to
                  the worker without the consent of the Ethiopian Ministry of
                  Labor and Skills (MoLS) shall not have any effect.
                </p>
                <p>
                  20. Settlement of disputes: all claims and complaints related
                  to the employment contract of the Employee shall be settled in
                  accordance with the companies' policies, rules, regulations,
                  and laws. In case of settlement, the matter shall be settled
                  amicably with the involvement of the appropriate Ethiopian
                  Mission (Embassy, permanent Mission, General Consulate Office
                  Charge de affairs or honorary Consulate General).
                </p>
                <p>
                  21. In case the amicable settlement fails, the matter shall be
                  submitted to the competent or appropriate government body in
                  (host country) or in Ethiopia at the option of the complaining
                  party
                </p>
                <p>
                  22. The Employee shall observe Employer's company rules and
                  abide by the pertinent laws of the host country.
                </p>
                <p>
                  23. The Employer is responsible to obtain the necessary entry
                  visa and legal work permit for the employee form the host
                  country.
                </p>
                <p>
                  24. Applicable law: Other terms and conditions of employment
                  which are consistent with the above provision shall be
                  governed by the permanent laws
                </p>
                <p>
                  25. If this contract of employment is violated by Employer,
                  the agency has agreed to be held responsible jointly and
                  severally.
                </p>
                <p>
                  26. This contract of employment is drawn in four (4) copies;
                  the Agency, the Employer, and the Employee will be provided
                  one each and the remaining one copy will remain at the
                  Ethiopian Ministry of Labor and Skills (MoLS).
                </p>
              </div>
              <div className="content-column arabic-text">
                <div className="overlay-image-right">
                  <img
                    src={agent.companyStamp || ""}
                    alt="Contract Watermark"
                  />
                </div>
                <div className="section-title">شروط العقد</div>
                {arabicTextArrays.slice(25).map((text, idx) => (
                  <p key={idx}>{text}</p>
                ))}
              </div>
            </div>
          </div>
        </div>
        <div className="kuwait-contract-page" ref={ref4}>
          {/* Signature Section */}
          <div className="signature-section">
            <div
              className="signature-row"
              style={{
                marginBottom: "20px",
              }}
            >
              <div className="signature-cell">
                <div
                  className=" ethiopic-text"
                  style={{
                    textAlign: "center",
                    fontWeight: "bold",
                    marginTop: "10px",
                  }}
                >
                  ይህን የስራ ዉል በ
                  {getAmharicCalendar(new Date(formattedDate), true)} ተፈራርመናል፡፡
                </div>
              </div>
              <div className="signature-cell">
                <div
                  style={{
                    textAlign: "center",
                    fontWeight: "bold",
                    marginTop: "10px",
                  }}
                >
                  We hereby sign this contract as of {formattedDate}.
                </div>
              </div>
              <div className="signature-cell arabic-text">
                <div
                  style={{
                    textAlign: "center",
                    fontWeight: "bold",
                    marginTop: "10px",
                  }}
                >
                  نحن نوقع هذا العقد بتاريخ {formattedDate}.
                </div>
              </div>
            </div>

            <div className="signature-row">
              <div
                className="signature-cell"
                style={{
                  width: "30%",
                  margin: "0 auto",
                  position: "relative",
                }}
              >
                <div className="signature-title ethiopic-text">
                  የግል ስራና ሰራተኛ አገናኝ ኤጀንሲ
                </div>
                <div className="signature-title">Private Employment Agency</div>
                <div className="signature-title arabic-text">
                  الوكالة الخاصة للتوظيف
                </div>
              </div>
              <div
                className="signature-cell"
                style={{
                  width: "70%",
                  margin: "0 auto",
                }}
              >
                <div
                  style={{
                    textAlign: "center",
                    fontWeight: "bold",
                    marginTop: "10px",
                    position: "relative",
                  }}
                >
                  {agency.displayName.toUpperCase()}
                  <div
                    className="overlay-image"
                    style={{ top: "0px", left: "0px" }}
                  >
                    <img
                      src={agency.companyStamp || ""}
                      alt="Contract Watermark"
                    />
                  </div>
                  <div
                    className="overlay-image-right"
                    style={{ top: "30px", right: "0px" }}
                  >
                    <img
                      src={agent.companyStamp || ""}
                      alt="Contract Watermark"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div
              className="signature-row"
              style={{
                marginBottom: "20px",
              }}
            >
              <div
                className="signature-cell"
                style={{
                  width: "30%",
                  margin: "0 auto",
                }}
              >
                <div className="signature-title ethiopic-text">የኤጀንሲው ተወካይ</div>
                <div className="signature-title">Agency representative</div>
                <div className="signature-title arabic-text">ممثل الوكالة</div>
              </div>
              <div
                className="signature-cell"
                style={{
                  width: "70%",
                  margin: "0 auto",
                }}
              >
                <div
                  style={{
                    textAlign: "center",
                    fontWeight: "bold",
                    marginTop: "10px",
                    position: "relative",
                  }}
                >
                  {agent.displayName.toUpperCase()}
                </div>
              </div>
            </div>

            <div className="signature-row">
              <div className="signature-cell">
                <div className="signature-title ethiopic-text">
                  የግል ስራና ሰራተኛ አገናኝ ኤጀንሲ ፊርማ
                </div>
                <div className="signature-title">
                  Private Employment Agency Signature
                </div>
                <div className="signature-title arabic-text">
                  توقيع وكالة التوظيف الخاصة
                </div>
                <div className="signature-box">
                  <div className="overlay-image-small">
                    <img
                      src={agency.companySignature || ""}
                      alt="Contract Watermark"
                    />
                  </div>
                </div>
              </div>
              <div className="signature-cell">
                <div className="signature-title ethiopic-text">የሰራተኛው ፊርማ</div>
                <div className="signature-title">Employee Signature</div>
                <div className="signature-title arabic-text">توقيع الموظف</div>
                <div className="signature-box">
                  <div className="overlay-image-small">
                    <img
                      src={agency.empSignature || ""}
                      alt="Contract Watermark"
                    />
                  </div>
                </div>
              </div>
            </div>

            <div className="signature-row">
              <div className="signature-cell">
                <div className="signature-title ethiopic-text">
                  የኤጀንሲው ተወካይ ፊርማ
                </div>
                <div className="signature-title">
                  Agency Representative Signature
                </div>
                <div className="signature-title arabic-text">
                  توقيع ممثل الوكالة
                </div>
                <div className="signature-box">
                  <div className="overlay-image-small">
                    <img
                      src={agent.companySignature || ""}
                      alt="Contract Watermark"
                    />
                  </div>
                </div>
              </div>
              <div className="signature-cell">
                <div className="signature-title ethiopic-text">የአሰሪው ፊርማ</div>
                <div className="signature-title">Employer Signature</div>
                <div className="signature-title arabic-text">توقيع الموظف</div>
                <div className="signature-box">
                  <div className="overlay-image-small">
                    <img
                      src={agent.empSignature || ""}
                      alt="Contract Watermark"
                    />
                  </div>
                </div>
              </div>
            </div>
          </div>

          {/* Witnesses Section */}
          <div className="witnesses-section">
            <div className="witnesses-content">
              <div className="witnesses-column ethiopic-text">
                <div className="witnesses-title">ምስክሮች</div>
                <div className="witness-field">
                  <span className="witness-label">1. ሙሉ ስም:</span>
                </div>
                <div className="witness-field">
                  <span className="witness-label">አድራሻ:</span>
                </div>
                <div className="witness-field">
                  <span className="witness-label">ስልክ ቁጥር:</span>
                </div>
                <div className="witness-field">
                  <span className="witness-label">ፊርማ:</span>
                </div>
                <div className="witness-field">
                  <span className="witness-label">2. ሙሉ ስም:</span>
                </div>
                <div className="witness-field">
                  <span className="witness-label">አድራሻ:</span>
                </div>
                <div className="witness-field">
                  <span className="witness-label">ስልክ ቁጥር:</span>
                </div>
                <div className="witness-field">
                  <span className="witness-label">ፊርማ:</span>
                </div>
              </div>
              <div className="witnesses-column">
                <div className="witnesses-title">Witnesses</div>
                <div className="witness-field">
                  <span className="witness-label">1. Full Name:</span>
                  <span className="witness-value">
                    {employee.firstTestimonialName || ""}
                  </span>
                </div>
                <div className="witness-field">
                  <span className="witness-label">Address:</span>
                  <span className="witness-value">
                    {employee.firstTestimonialAddress || ""}
                  </span>
                </div>
                <div className="witness-field">
                  <span className="witness-label">Phone Number:</span>
                  <span className="witness-value">
                    {employee.firstTestimonialTelephone || ""}
                  </span>
                </div>
                <div className="witness-field">
                  <span className="witness-label">Signature:</span>
                  <span className="witness-value">
                    <div
                      className="overlay-image-small"
                      style={{ height: "50px", width: "100px" }}
                    >
                      <img
                        src={agency.testimonySignature || ""}
                        alt="Contract Watermark"
                      />
                    </div>
                  </span>
                </div>
                <div className="witness-field">
                  <span className="witness-label">2. Full Name:</span>
                  <span className="witness-value">
                    {employee.secondTestimonialName || ""}
                  </span>
                </div>
                <div className="witness-field">
                  <span className="witness-label">Address:</span>
                  <span className="witness-value">
                    {employee.secondTestimonialAddress || ""}
                  </span>
                </div>
                <div className="witness-field">
                  <span className="witness-label">Phone Number:</span>
                  <span className="witness-value">
                    {employee.secondTestimonialTelephone || ""}
                  </span>
                </div>
                <div className="witness-field">
                  <span className="witness-label">Signature:</span>
                  <span className="witness-value">
                    <div
                      className="overlay-image-small"
                      style={{ height: "50px", width: "100px" }}
                    >
                      <img
                        src={agency.testimonySignature2 || ""}
                        alt="Contract Watermark"
                      />
                    </div>
                  </span>
                </div>
              </div>
              <div className="witnesses-column arabic-text">
                <div className="witnesses-title">الشهود</div>
                <div className="witness-field">
                  <span className="witness-label">1. الاسم الكامل</span>
                </div>
                <div className="witness-field">
                  <span className="witness-label">العنوان</span>
                </div>
                <div className="witness-field">
                  <span className="witness-label">رقم الهاتف</span>
                </div>
                <div className="witness-field">
                  <span className="witness-label">التوقيع</span>
                </div>
                <div className="witness-field">
                  <span className="witness-label">2. الاسم الكامل</span>
                </div>
                <div className="witness-field">
                  <span className="witness-label">العنوان</span>
                </div>
                <div className="witness-field">
                  <span className="witness-label">رقم الهاتف</span>
                </div>
                <div className="witness-field">
                  <span className="witness-label">التوقيع</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </>
    );
  } else {
    return (
      <div className="error-page">
        <h2>Please fill all the required Data</h2>
      </div>
    );
  }
};
